"use client";

/**
 * Google tag loader — the piece that was missing.
 *
 * `lib/analytics/gtag.ts` has always held a full GA4 e-commerce event layer
 * (view_item, add_to_cart, begin_checkout, purchase…) wired into the product
 * cards, cart drawer and checkout — but every call is guarded by
 * `if (window.gtag)` and NOTHING ever loaded gtag.js, so not one event has
 * ever been sent. This component loads it, so all of that starts working.
 *
 * Per brand: the IDs come from `brands.tracking` (each brand owns its own
 * Google accounts) and arrive on the storefront query that already runs, so
 * mounting this costs no extra Convex reads. Renders nothing when a brand has
 * configured no IDs — no tag, no cookies, no third-party request.
 */

import Script from "next/script";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

export interface BrandTracking {
  ga4MeasurementId?: string | null;
  adsConversionId?: string | null;
  adsPurchaseLabel?: string | null;
}

export default function GoogleTag({ tracking }: { tracking?: BrandTracking | null }) {
  const ga4 = tracking?.ga4MeasurementId || "";
  const ads = tracking?.adsConversionId || "";
  // gtag.js is loaded once against the first configured ID; every other ID is
  // registered with its own gtag("config"), which is how Google supports GA4
  // and Ads on one tag.
  const primaryId = ga4 || ads;
  const pathname = usePathname();
  const lastPath = useRef<string | null>(null);

  // SPA page views: gtag only auto-sends one on first load, so client-side
  // route changes need an explicit page_view or shop navigation is invisible.
  useEffect(() => {
    if (!primaryId || !pathname || typeof window === "undefined") return;
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    const w = window as any;
    if (typeof w.gtag !== "function") return;
    if (ga4) {
      w.gtag("event", "page_view", {
        send_to: ga4,
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname, primaryId, ga4]);

  if (!primaryId) return null;

  return (
    <>
      <Script
        id="gtag-src"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`}
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;
          gtag('js', new Date());
          ${ga4 ? `gtag('config', '${ga4}');` : ""}
          ${ads ? `gtag('config', '${ads}');` : ""}
        `}
      </Script>
    </>
  );
}

/**
 * Fire a purchase on both GA4 and Google Ads.
 *
 * Called once from the checkout success page with the REAL order total, so
 * Google Ads optimises against actual revenue instead of counting bare
 * conversions. Safe to call when nothing is configured (no-ops), and
 * de-duplicated per order via `transaction_id` + sessionStorage so a refresh
 * of the success page cannot double-count a sale.
 */
export function trackPurchaseConversion(params: {
  tracking?: BrandTracking | null;
  orderNumber: string;
  value: number;
  currency?: string;
  items?: Array<{ id: string; name: string; price: number; quantity: number; category?: string }>;
}): void {
  if (typeof window === "undefined") return;
  const w = window as any;
  if (typeof w.gtag !== "function") return;

  const { tracking, orderNumber, value } = params;
  const currency = params.currency || "UGX";
  if (!orderNumber) return;

  // Refresh/back-navigation guard — one purchase per order per browser.
  const key = `kuttos_purchase_sent_${orderNumber}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // Private mode / storage blocked: still send, just without the guard.
  }

  const ga4 = tracking?.ga4MeasurementId;
  const ads = tracking?.adsConversionId;
  const label = tracking?.adsPurchaseLabel;

  if (ga4) {
    w.gtag("event", "purchase", {
      send_to: ga4,
      transaction_id: orderNumber,
      value,
      currency,
      items: (params.items || []).map((i) => ({
        item_id: i.id,
        item_name: i.name,
        item_category: i.category || "Uncategorized",
        price: i.price,
        quantity: i.quantity,
      })),
    });
  }

  // Google Ads conversion — needs BOTH the conversion id and its label.
  if (ads && label) {
    w.gtag("event", "conversion", {
      send_to: `${ads}/${label}`,
      value,
      currency,
      transaction_id: orderNumber,
    });
  }
}
